import React, { FC, useEffect, useRef, useState } from 'react';
import { Money } from 'shared/types/product';
import Button from 'components/commercetools-ui/atoms/button';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useI18n from 'helpers/hooks/useI18n';
import { classnames } from 'helpers/utils/classnames';

type StickyBuyBarProps = {
  /** Whether the main price block has scrolled above the viewport. */
  visible: boolean;
  productName: string;
  /** All-in price per item (base + finish upcharge), matching the main price block. */
  price?: Money;
  /** List price, only when the variant is discounted. */
  listPrice?: Money;
  ctaLabel: string;
  disabled?: boolean;
  loading?: boolean;
  added?: boolean;
  onAddToCart: () => void;
};

/**
 * Docks price + Add to Cart beneath the site header once the main price block scrolls out of
 * view, so the buy action stays reachable down the page.
 *
 * Sits at `--header-height` rather than a measured value: the header sets that variable, which
 * sidesteps having to find the visible header among responsive variants (a dual desktop/mobile
 * header setup makes `querySelector('header')` return the hidden one, docking the bar at 0 and
 * hiding it behind the real header).
 */
const StickyBuyBar: FC<StickyBuyBarProps> = ({
  visible,
  productName,
  price,
  listPrice,
  ctaLabel,
  disabled,
  loading,
  added,
  onAddToCart,
}) => {
  const { locale, currency } = useI18n();

  // Measure the header that is actually on screen rather than trusting --header-height: that
  // variable is set from one header ref's offsetHeight, which does not match the visible header
  // at every breakpoint (it read 176px against a 77px header), leaving the bar floating below it.
  const [headerOffset, setHeaderOffset] = useState(0);
  useEffect(() => {
    const findVisibleHeader = () =>
      [...document.querySelectorAll('header')].find(
        (header) => header.offsetParent !== null && header.getBoundingClientRect().height > 0,
      );

    const measure = () => setHeaderOffset(Math.round(findVisibleHeader()?.getBoundingClientRect().height ?? 0));
    measure();

    window.addEventListener('resize', measure);
    // The visible header can change height without a resize (condensing on scroll, promo bars).
    const header = findVisibleHeader();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
    if (header && observer) observer.observe(header);

    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, []);

  const barRef = useRef<HTMLDivElement>(null);

  // The bar's height whether or not it is showing, so pinned PDP columns reserve room for it up front
  // (moving them as the bar appeared made them drift with the page and look stuck mid-scroll) and the
  // hide check can wait it out. Kept current as the bar resizes (price wraps, breakpoints) and removed
  // on unmount so the next page doesn't start from this one's value.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const root = document.documentElement.style;
    const measure = () => {
      const reserve = Math.round(bar.getBoundingClientRect().height);
      if (reserve) root.setProperty('--buy-bar-reserve', `${reserve}px`);
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      root.removeProperty('--buy-bar-reserve');
    };
  }, [price]);

  if (!price) return null;

  const isDiscounted = (listPrice?.centAmount ?? 0) > (price.centAmount ?? 0);

  return (
    <div
      ref={barRef}
      // Hidden from assistive tech AND non-interactive when off-screen — otherwise the CTA stays
      // keyboard-focusable and clickable while invisible.
      aria-hidden={!visible}
      data-visible={visible}
      className={classnames(
        // bg-white, not bg-neutral-white: the latter resolves to an undefined CSS variable, so the
        // bar rendered transparent and page content showed through it.
        'fixed inset-x-0 top-0 z-40 border-b border-stroke-primary bg-white font-gt shadow-sm transition-transform duration-200',
        // Never intercept clicks while off-screen or mid-transition.
        visible ? '' : 'pointer-events-none',
      )}
      style={{
        // Anchored at the top and translated down by the measured header height, rather than
        // anchored at that height and translated -100%: the latter only lifts the bar by its own
        // height, leaving it parked on screen below the header instead of clearing the viewport.
        transform: visible ? `translateY(${headerOffset}px)` : 'translateY(-100%)',
      }}
    >
      <div className="container flex items-center gap-16 py-12">
        <span className="min-w-0 flex-1 truncate text-14 font-semibold text-primary">{productName}</span>

        <span className="flex items-baseline gap-8">
          <span className={classnames('text-18 font-semibold', isDiscounted ? 'text-error' : 'text-primary')}>
            {CurrencyHelpers.formatForCurrency(price, locale, currency)}
          </span>
          {isDiscounted && (
            <span className="hidden text-14 text-tertiary line-through md:inline">
              {CurrencyHelpers.formatForCurrency(listPrice as Money, locale, currency)}
            </span>
          )}
        </span>

        <Button
          className="whitespace-nowrap rounded-sm text-14 font-medium"
          variant="secondary-black"
          onClick={onAddToCart}
          loading={loading}
          added={added}
          disabled={disabled || !visible}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};

export default StickyBuyBar;
